/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Currency Utilities Module
 *
 * Provides currency symbols and formatting utilities.
 *
 * For exchange rates and currency conversion, use:
 * - CurrencyContext and useCurrency hook for React components
 * - lib/api/rates.ts for API-level operations
 *
 * @module lib/currencies
 */

// ============================================================================
// Currency Symbol Definitions
// ============================================================================

/**
 * Currency definition with code and symbol
 */
interface CurrencyDefinition {
  /** ISO 4217 currency code */
  title: string;
  /** Currency symbol for display */
  value: string;
}

/**
 * Primary currencies with symbols
 * Used for display purposes only - not for conversion
 */
const CURRENCIES: CurrencyDefinition[] = [
  {
    title: "USD",
    value: "$",
  },
  {
    title: "EUR",
    value: "€",
  },
  {
    title: "JPY",
    value: "¥",
  },
  {
    title: "GBP",
    value: "£",
  },
  {
    title: "AUD",
    value: "A$",
  },
  {
    title: "CAD",
    value: "C$",
  },
  {
    title: "CHF",
    value: "CHF",
  },
  {
    title: "CNY",
    value: "¥",
  },
  {
    title: "SEK",
    value: "kr",
  },
  {
    title: "NZD",
    value: "NZ$",
  },
  {
    title: "INR",
    value: "₹",
  },
  {
    title: "BRL",
    value: "R$",
  },
  {
    title: "MXN",
    value: "$",
  },
  {
    title: "KRW",
    value: "₩",
  },
  {
    title: "SGD",
    value: "S$",
  },
  {
    title: "HKD",
    value: "HK$",
  },
  {
    title: "NOK",
    value: "kr",
  },
  {
    title: "DKK",
    value: "kr",
  },
  {
    title: "PLN",
    value: "zł",
  },
  {
    title: "THB",
    value: "฿",
  },
  {
    title: "IDR",
    value: "Rp",
  },
  {
    title: "TRY",
    value: "₺",
  },
  {
    title: "RUB",
    value: "₽",
  },
  {
    title: "ZAR",
    value: "R",
  },
  {
    title: "AED",
    value: "د.إ",
  },
  {
    title: "SAR",
    value: "﷼",
  },
  {
    title: "PHP",
    value: "₱",
  },
  {
    title: "MYR",
    value: "RM",
  },
  {
    title: "CZK",
    value: "Kč",
  },
  {
    title: "ILS",
    value: "₪",
  },
  {
    title: "CLP",
    value: "$",
  },
  {
    title: "PKR",
    value: "₨",
  },
  {
    title: "COP",
    value: "$",
  },
  {
    title: "TWD",
    value: "NT$",
  },
  {
    title: "RON",
    value: "lei",
  },
  {
    title: "HUF",
    value: "Ft",
  },
  {
    title: "BGN",
    value: "лв",
  },
  {
    title: "VND",
    value: "₫",
  },
];

// ============================================================================
// Symbol Lookup Functions
// ============================================================================

/**
 * Get currency symbol for a given currency code
 *
 * @param currencyCode - ISO 4217 currency code (e.g., "USD", "EUR")
 * @returns Currency symbol (e.g., "$", "€") or the code itself if not found
 *
 * @example
 * ```ts
 * getCurrencySymbol("USD") // "$"
 * getCurrencySymbol("EUR") // "€"
 * getCurrencySymbol("XYZ") // "XYZ" (fallback to code)
 * ```
 */
export const getCurrencySymbol = (currencyCode: string): string => {
  if (!currencyCode) return "$";
  const normalizedCode = currencyCode.toUpperCase();
  const currency = CURRENCIES.find((c) => c.title === normalizedCode);
  return currency?.value || normalizedCode;
};

/**
 * Check if a currency code is in our symbol list
 *
 * @param currencyCode - ISO 4217 currency code
 * @returns true if we have a symbol for this currency
 */
const hasCurrencySymbol = (currencyCode: string): boolean => {
  if (!currencyCode) return false;
  const normalizedCode = currencyCode.toUpperCase();
  return CURRENCIES.some((c) => c.title === normalizedCode);
};

/**
 * Get currency definition by code
 *
 * @param currencyCode - ISO 4217 currency code
 * @returns Currency definition or undefined if not found
 */
const getCurrencyByCode = (
  currencyCode: string,
): CurrencyDefinition | undefined => {
  if (!currencyCode) return undefined;
  const normalizedCode = currencyCode.toUpperCase();
  return CURRENCIES.find((c) => c.title === normalizedCode);
};

// ============================================================================
// Exports
// ============================================================================

export default {
  CURRENCIES,
  getCurrencySymbol,
  hasCurrencySymbol,
  getCurrencyByCode,
};
